"use client";

import { trackCoreCollectionsCollectionTileView } from "@faire/web-api/events/coreCollections/view/collectionTile";
import React, { PropsWithChildren, useCallback, useRef, useState } from "react";

// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { IntersectionObserverProvider } from "@faire/retailer/hooks/ProductTileImpression/__internal__/ProductTileViewImpressionContext";
// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { UnmountProductTileProvider } from "@faire/retailer/hooks/ProductTileImpression/__internal__/UnmountProductTileContext";
import { useVisibilityChangeEvent } from "@faire/retailer/hooks/useVisibilityChangeEvent";
import { getIsRobot } from "@faire/retailer/serialized-data/getIsRobot";

// eslint-disable-next-line @faire/enforce-internal-folder-encapsulation
import { ProductTileViewImpressionUpperThresholdProvider } from "../ProductTileImpression/__internal__/ViewportUpperThreshold";

import { ICollectionTileImpressionContextValue } from "./CollectionTileClickImpressionContext";

type IDwellTimerMap = Record<
  string,
  {
    start: number;
    maxThreshold: number;
    brandPromoToken?: string;
  }
>;

export const CollectionTileViewImpressionProvider: React.FC<
  PropsWithChildren<ICollectionTileImpressionContextValue>
> = ({
  children,
  contextToken,
  requestId,
  screenContext,
  containerContext,
}) => {
  const [viewportThresholdHeightFromTop, setViewportThresholdHeightFromTop] =
    useState<number>(0);

  const rootMargin = `${-viewportThresholdHeightFromTop}px 0px 0px 0px`;
  const dwellTimerMapRef = useRef<IDwellTimerMap>({});

  const handleProductTileUnmount = useCallback(
    (collectionToken: string) => {
      reportDwellTimerForCollectionTile(
        collectionToken,
        contextToken ?? "",
        requestId ?? "",
        dwellTimerMapRef,
        screenContext ?? "",
        containerContext ?? ""
      );
    },
    [containerContext, contextToken, requestId, screenContext]
  );

  useVisibilityChangeEvent((visibilityState) => {
    if (visibilityState === "hidden") {
      const collectionTokens = Object.keys(dwellTimerMapRef.current);
      collectionTokens.forEach((collectionToken) => {
        reportDwellTimerForCollectionTile(
          collectionToken,
          contextToken ?? "",
          requestId ?? "",
          dwellTimerMapRef,
          screenContext ?? "",
          containerContext ?? ""
        );
      });
    }
  });

  const handleIntersectionObserverCallback = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const collectionToken =
          entry.target.getAttribute("data-collection-token") ?? "";
        const brandPromoToken =
          entry.target.getAttribute("data-brand-promo-token") ?? "";
        if (
          entry.isIntersecting &&
          !dwellTimerMapRef.current[collectionToken]
        ) {
          dwellTimerMapRef.current[collectionToken] = {
            start: Date.now(),
            maxThreshold: entry.intersectionRatio,
            brandPromoToken,
          };
        } else if (
          !entry.isIntersecting &&
          dwellTimerMapRef.current[collectionToken]
        ) {
          reportDwellTimerForCollectionTile(
            collectionToken,
            contextToken ?? "",
            requestId ?? "",
            dwellTimerMapRef,
            screenContext ?? "",
            containerContext ?? ""
          );
        } else if (
          entry.isIntersecting &&
          dwellTimerMapRef.current[collectionToken]
        ) {
          if (
            (dwellTimerMapRef.current[collectionToken]?.maxThreshold ?? 0) <
            entry.intersectionRatio
          ) {
            dwellTimerMapRef.current[collectionToken]!.maxThreshold =
              entry.intersectionRatio;
          }
        }
      });
    },
    [containerContext, contextToken, requestId, screenContext]
  );

  if (getIsRobot()) {
    return children;
  }

  return (
    <UnmountProductTileProvider onUnmount={handleProductTileUnmount}>
      <ProductTileViewImpressionUpperThresholdProvider
        heightFromTop={viewportThresholdHeightFromTop}
        setHeightFromTop={setViewportThresholdHeightFromTop}
      >
        <IntersectionObserverProvider
          callback={handleIntersectionObserverCallback}
          options={{
            threshold: TWO_PERCENT_THRESHOLD,
            rootMargin,
          }}
        >
          {children}
        </IntersectionObserverProvider>
      </ProductTileViewImpressionUpperThresholdProvider>
    </UnmountProductTileProvider>
  );
};

const reportDwellTimerForCollectionTile = (
  collectionToken: string,
  contextToken: string,
  requestId: string,
  dwellTimerMapRef: React.MutableRefObject<IDwellTimerMap>,
  screenContext?: string,
  containerContext?: string
) => {
  if (dwellTimerMapRef.current[collectionToken]) {
    const { maxThreshold, brandPromoToken } =
      dwellTimerMapRef.current[collectionToken]!;
    trackCoreCollectionsCollectionTileView(
      contextToken,
      collectionToken,
      requestId ?? "",
      screenContext ?? "",
      containerContext ?? "",
      maxThreshold.toString() ?? "",
      brandPromoToken ?? ""
    );

    delete dwellTimerMapRef.current[collectionToken];
  }
};

const TWO_PERCENT_THRESHOLD = new Array(51)
  .fill(0)
  .map((_, index) => index * 2)
  // this ensures no 0.15000000001 values
  .map((percent) => percent / 100);
